import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  roleAtLeast,
  userHasModule,
  type AppModule,
  type AuthenticatedUser,
  type Role,
} from '@vce/shared';
import { api } from '@/lib/api';
import { clearSession, loadSession, saveSession } from '@/lib/session';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  login(email: string, password: string): Promise<AuthenticatedUser>;
  logout(): Promise<void>;
  can(role: Role): boolean;
  hasModule(module: AppModule): boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function withAccessDefaults(user: AuthenticatedUser): AuthenticatedUser {
  return {
    ...user,
    isTester: user.isTester ?? false,
    tenantIds: user.tenantIds ?? [],
    modules: user.modules ?? [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(() => {
    const session = loadSession()?.user;
    return session ? withAccessDefaults(session) : null;
  });

  // Mantém as abas em sincronia: sair numa aba precisa derrubar as outras.
  useEffect(() => {
    const sync = (): void => {
      const session = loadSession()?.user;
      setUser(session ? withAccessDefaults(session) : null);
    };
    window.addEventListener('vce:session', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('vce:session', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Recarrega papel/módulos do servidor para a sessão local não ficar velha.
  useEffect(() => {
    if (!loadSession()) return;
    void api.auth
      .me()
      .then((fresh) => {
        const session = loadSession();
        if (!session) return;
        const next = withAccessDefaults(fresh);
        saveSession({ ...session, user: next });
        setUser(next);
      })
      .catch(() => undefined);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.auth.login(email, password);
    const next = withAccessDefaults(response.user);
    saveSession({ ...response, user: next });
    setUser(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    const session = loadSession();
    if (session) await api.auth.logout(session.refreshToken).catch(() => undefined);
    clearSession();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      login,
      logout,
      can: (role: Role) => (user ? roleAtLeast(user.role, role) : false),
      hasModule: (module: AppModule) => (user ? userHasModule(user, module) : false),
    }),
    [user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return context;
}
